import type { AcceptedEdge, EdgeContext, EdgeDirection } from './schema/edge.js';
import { compareRelationshipStartsDescending, type QualifiedTime } from './schema/time.js';

export const RELATIONSHIP_PHASE_IDENTITY_VERSION = 1;

type SemanticRelationship = Pick<
  AcceptedEdge,
  | 'source'
  | 'target'
  | 'lens'
  | 'relation'
  | 'propertyId'
  | 'directed'
  | 'direction'
  | 'context'
  | 'start'
  | 'end'
  | 'status'
>;

export type RepresentativeRelationship = {
  id: string;
  relation: AcceptedEdge['relation'];
  status: AcceptedEdge['status'];
  sourceIds?: readonly string[] | undefined;
  start?: QualifiedTime | undefined;
};

export type EndpointRelativeRelationship = {
  source: string;
  target: string;
  relation: AcceptedEdge['relation'];
  label?: string | undefined;
  direction?: EdgeDirection | undefined;
};

function counterpartRole(
  relationship: EndpointRelativeRelationship,
  personId: string,
): string | undefined {
  const direction = relationship.direction;
  if (direction === undefined) {
    return undefined;
  }
  if (direction.subject === personId) {
    return direction.objectRole;
  }
  if (direction.object === personId) {
    return direction.subjectRole;
  }
  return undefined;
}

function roleLabel(role: string): string {
  switch (role) {
    case 'parent':
      return 'Parent';
    case 'child':
      return 'Child';
    case 'stepparent':
      return 'Stepparent';
    case 'stepchild':
      return 'Stepchild';
    case 'godparent':
      return 'Godparent';
    case 'godchild':
      return 'Godchild';
    default:
      return role
        .split(/[\s_-]+/u)
        .filter(Boolean)
        .map((part) => `${part[0]?.toLocaleUpperCase('en-US') ?? ''}${part.slice(1)}`)
        .join(' ');
  }
}

function genericRelationshipLabel(relation: AcceptedEdge['relation']): string {
  switch (relation) {
    case 'spouse':
      return 'Spouse';
    case 'unmarried-partner':
      return 'Partner';
    case 'parent-child':
      return 'Parent / child';
    case 'stepparent':
      return 'Stepparent / stepchild';
    case 'sibling':
      return 'Sibling';
    case 'relative':
      return 'Relative';
    case 'godparent':
      return 'Godparent';
    case 'professional-collaborator':
      return 'Professional collaborator';
    case 'student-of':
      return 'Student';
    case 'doctoral-advisor':
      return 'Doctoral advisor';
    case 'appointed-by':
      return 'Appointed by';
    case 'creative-collaborator':
      return 'Creative collaborator';
    case 'organizational-collaborator':
      return 'Organizational collaborator';
  }
}

/**
 * Names a relationship from the selected endpoint's point of view. Directional
 * roles come from the canonical direction contract rather than runtime array order.
 */
export function endpointRelativeRelationshipLabel(
  relationship: EndpointRelativeRelationship,
  personId: string,
): string {
  switch (relationship.relation) {
    case 'spouse':
      return 'Spouse';
    case 'unmarried-partner':
      return 'Partner';
    case 'sibling':
      return 'Sibling';
    case 'relative':
      return 'Relative';
    case 'parent-child':
    case 'stepparent':
    case 'godparent': {
      const role = counterpartRole(relationship, personId);
      return role === undefined
        ? (relationship.label ?? genericRelationshipLabel(relationship.relation))
        : roleLabel(role);
    }
    case 'professional-collaborator':
    case 'student-of':
    case 'doctoral-advisor':
    case 'appointed-by':
    case 'creative-collaborator':
    case 'organizational-collaborator':
      return relationship.label ?? genericRelationshipLabel(relationship.relation);
  }
}

function orderedPair(left: string, right: string): readonly [string, string] {
  return left.localeCompare(right) <= 0 ? [left, right] : [right, left];
}

export function relationshipPairKey(left: string, right: string): string {
  return orderedPair(left, right).join('|');
}

export function relationshipConnectionKey(
  left: string,
  right: string,
  lens: AcceptedEdge['lens'],
): string {
  return `${relationshipPairKey(left, right)}|${lens}`;
}

function normalizedTime(time: QualifiedTime | undefined): unknown {
  if (time === undefined) return null;
  return {
    iso: time.iso,
    precision: time.precision,
    circa: time.circa === true,
    earliest: time.earliest ?? null,
    latest: time.latest ?? null,
  };
}

function normalizedDirection(
  pair: readonly [string, string],
  direction: EdgeDirection | undefined,
): unknown {
  if (direction === undefined) return null;
  const roleFor = (personId: string) => {
    if (direction.subject === personId) return direction.subjectRole;
    if (direction.object === personId) return direction.objectRole;
    return null;
  };
  return [roleFor(pair[0]), roleFor(pair[1])];
}

function normalizedContext(context: EdgeContext | undefined): unknown {
  if (context === undefined) return null;
  return {
    identity: context.id ?? context.label.normalize('NFKC').toLocaleLowerCase('en-US'),
    roles: [...(context.roles ?? [])].sort((left, right) => left.localeCompare(right)),
    participantRoles: [...(context.participantRoles ?? [])]
      .map((participant) => ({
        personId: participant.personId,
        roles: [...participant.roles].sort((left, right) => left.localeCompare(right)),
      }))
      .sort((left, right) => left.personId.localeCompare(right.personId)),
  };
}

function semanticBase(relationship: SemanticRelationship): Record<string, unknown> {
  const pair = orderedPair(relationship.source, relationship.target);
  return {
    version: RELATIONSHIP_PHASE_IDENTITY_VERSION,
    pair,
    lens: relationship.lens,
    relation: relationship.relation,
    propertyId: relationship.propertyId,
    directed: relationship.directed,
    direction: normalizedDirection(pair, relationship.direction),
    context: normalizedContext(relationship.context),
  };
}

/**
 * Stable relationship meaning without dates or publication status. This groups
 * repeated temporal phases while retaining directional and contextual meaning.
 */
export function relationshipMeaningIdentity(relationship: SemanticRelationship): string {
  return JSON.stringify(semanticBase(relationship));
}

/**
 * Stable accepted phase identity. Sources, statement ordering, names, URLs, and
 * display labels are deliberately excluded so equivalent claims remain detectable.
 */
export function relationshipPhaseIdentity(relationship: SemanticRelationship): string {
  return JSON.stringify({
    ...semanticBase(relationship),
    start: normalizedTime(relationship.start),
    end: normalizedTime(relationship.end),
    status: relationship.status,
  });
}

/** The same phase identity without status, used to diagnose status conflicts. */
export function relationshipPhaseStatusIdentity(relationship: SemanticRelationship): string {
  return JSON.stringify({
    ...semanticBase(relationship),
    start: normalizedTime(relationship.start),
    end: normalizedTime(relationship.end),
  });
}

const RELATION_PRIORITY: Readonly<Record<AcceptedEdge['relation'], number>> = {
  spouse: 0,
  'parent-child': 1,
  stepparent: 2,
  sibling: 3,
  godparent: 4,
  relative: 5,
  'unmarried-partner': 6,
  'professional-collaborator': 7,
  'student-of': 8,
  'doctoral-advisor': 9,
  'appointed-by': 10,
  'creative-collaborator': 11,
  'organizational-collaborator': 12,
};

const PROVENANCE_PRIORITY: Readonly<Record<AcceptedEdge['status'], number>> = {
  wikidata: 0,
  documented: 0,
  reported: 1,
  disputed: 2,
  rumored: 3,
};

export function isPrimaryRelationshipProvenance(status: AcceptedEdge['status']): boolean {
  return status === 'wikidata' || status === 'documented';
}

/**
 * Shared representative rule for parallel accepted records. Primary Wikidata
 * provenance and better-sourced records win; semantic/temporal tie-breakers
 * are stable. `documented` is the historical equivalent of `wikidata`.
 */
export function compareRepresentativeRelationships(
  left: RepresentativeRelationship,
  right: RepresentativeRelationship,
): number {
  const statusOrder = PROVENANCE_PRIORITY[left.status] - PROVENANCE_PRIORITY[right.status];
  const sourceOrder = (right.sourceIds?.length ?? 0) - (left.sourceIds?.length ?? 0);
  return (
    statusOrder ||
    sourceOrder ||
    RELATION_PRIORITY[left.relation] - RELATION_PRIORITY[right.relation] ||
    compareRelationshipStartsDescending(left, right) ||
    left.id.localeCompare(right.id)
  );
}
